import * as XLSX from 'xlsx';

export interface ParsedWorkbook {
  headers: string[];
  rows: Record<string, string>[];
}

function isBlankRow(values: unknown[]): boolean {
  return !values.some((v) => v !== null && v !== undefined && String(v).trim() !== '');
}

function sheetToParsed(sheet: XLSX.WorkSheet): ParsedWorkbook {
  const matrix = XLSX.utils.sheet_to_json<(string | number | null | undefined)[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  }) as unknown[][];

  if (!matrix.length) {
    throw new Error('The file is empty');
  }

  const headerRow = matrix[0] as unknown[];
  const headers = headerRow.map((h) => String(h ?? '').trim());
  if (!headers.some(Boolean)) {
    throw new Error('Header row is missing');
  }

  const rows: Record<string, string>[] = [];
  for (let i = 1; i < matrix.length; i++) {
    const row = matrix[i] as unknown[];
    if (!row || isBlankRow(row)) continue;
    const item: Record<string, string> = {};
    headers.forEach((header, idx) => {
      if (!header) return;
      const val = row[idx];
      item[header] = val === null || val === undefined ? '' : String(val).trim();
    });
    rows.push(item);
  }

  return { headers: headers.filter(Boolean), rows };
}

export async function parseWorkbook(file: File): Promise<ParsedWorkbook> {
  const name = file.name.toLowerCase();
  if (!name.endsWith('.csv') && !name.endsWith('.xlsx')) {
    throw new Error('Unsupported file type. Please upload a CSV or XLSX file.');
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error('The file has no sheets');
  }
  return sheetToParsed(workbook.Sheets[firstSheetName]);
}
